import _cloneDeep from 'lodash/cloneDeep.js';
import { v4 as uuid } from 'uuid';

/**
 * Возвращает ширину скроллбара для document.body
 * @returns number
 */
export const getScrollbarWidth = block => {
    if (block) {
        const inititalOverflowValue = block.style.getPropertyValue('overflow-y');
        const inititalOverflowPriority = block.style.getPropertyPriority('overflow-y');

        block.style.setProperty('overflow-y', 'scroll', 'important');

        const scrollWidth = block.offsetWidth - block.clientWidth;

        if (inititalOverflowValue) {
            block.style.setProperty(
                'overflow-y',
                inititalOverflowValue,
                inititalOverflowPriority,
            );
        } else {
            block.style.removeProperty('overflow-y');
        }

        return scrollWidth;
    }

    const div = document.createElement('div');

    div.style.overflowY = 'scroll';
    div.style.position = 'absolute';
    div.style.width = '100vw';
    div.style.height = '100vh';
    div.style.visibility = 'hidden';

    document.body.appendChild(div);

    const scrollWidth = div.offsetWidth - div.clientWidth;

    document.body.removeChild(div);

    return scrollWidth;
};

/**
 * Возвращает слово в правильном склонении
 * pluralize(1, ['балл', 'балла', 'баллов']) // 1 балл
 * pluralize(2, ['балл', 'балла', 'баллов']) // 2 балла
 * pluralize(5, ['балл', 'балла', 'баллов']) // 5 баллов
 * @param {number} number
 * @param {array} titles
 * @returns string
 */
export const pluralize = (number, titles) => {
    const numberAbs = Math.abs(number);

    return titles[
        (numberAbs % 100 > 4 && numberAbs % 100 < 20)
            ? 2
            : [2, 0, 1, 1, 1, 2][(numberAbs % 10 < 5) ? numberAbs % 10 : 5]
    ];
};

/**
 * Возвращает случайный uuid
 * @returns string
 */
export const getRandomId = () => uuid();

/**
 * Добавляет паузу на указанное количество милисекунд
 * await wait(1000) // пауза на 1 секунду
 * @param {number} delay
 * @returns Promise
 */
export const wait = delay => new Promise(resolve => {
    setTimeout(resolve, delay);
});

/**
 * Приводит значение к числу
 * @param {*} value
 * @returns number
 */
export const toNumber = value => parseFloat(value);

/**
 * Приводит число к переданным единицам измерения, либо вернет само значение
 * @param {*} value
 * @param {string} unit
 * @returns undefined | null | string
 */
export const converToUnit = (value, unit = 'px') => {
    if (typeof value === 'undefined' || value === null || value === '') {
        return value;
    } else if (isNaN(Number(value))) {
        return String(value);
    }

    return `${Number(value)}${unit}`;
};

/**
 * Возвращает массив родителей для DOM ноды
 * @param {object} node
 * @param {array} memo
 * @returns array
 */
export const getParents = (node = {}, memo = []) => {
    const { parentNode } = node;

    if (!parentNode) {
        return memo;
    }

    return getParents(parentNode, [...memo, parentNode]);
};

/**
 * Возвращает массив DOM элементов,
 * на которых сработало событие начиная с event.target
 * @param {object} event
 * @returns array
 */
export const getEventPath = (event = {}) => {
    const path = (
        (event.composedPath && event.composedPath()) ||
        event.path ||
        null
    );
    const { target } = event;

    if (path !== null) {
        // Safari doesn't include Window, but it should.
        return path.includes(window) ? [...path, window] : path;
    }

    if (target === window) {
        return [window];
    }

    return [target, ...getParents(target), window];
};

/**
 * Возвращает новый объект без переданных ключей
 * @param {object} object
 * @param {array} keysArray
 * @returns object
 */
export const deleteKeysFromObject = (object, keysArray = []) => {
    if (!keysArray.length) return object;

    const updatedObject = _cloneDeep(object);

    keysArray.forEach(key => {
        if (Object.hasOwnProperty.call(updatedObject, key)) {
            delete updatedObject[key];
        }
    });

    return updatedObject;
};

const COMMENT_NODE_TYPE = 8;

/**
 * Проверяет является ли переданный DOM элемент комментарием
 * @param {HTMLElement} el
 * @returns boolean
 */
export const isElementCommentNode = el => el.nodeType === COMMENT_NODE_TYPE;

/**
 * Возвращает первый элемент, имеющий тег, из переданного слота
 * @param {array} slot
 * @returns null | HTMLElement
 */
export const getFirstElement = slot => {
    if (!Array.isArray(slot)) return null;

    return slot.find(node => node.tag) || null;
};

/**
 * Форматирует размер в байтах
 * @param {number|string} bytes
 * @param {number} decimals
 * @param {array} sizes
 * @param {number} K
 * @returns undefined | string
 */
export const formatBytes = (
    bytes,
    decimals = 2,
    sizes = [' Б', ' КБ', ' МБ', ' ГБ', ' ТБ', ' ПБ'],
    K = 1024,
) => {
    let error = '';

    if (isNaN(toNumber(bytes))) {
        error = `Bytes can't be converted to a number. You provided "${bytes}"`;
    } else if (toNumber(bytes) < 0) {
        error = `Bytes can't be less than 0. You provided "${bytes}"`;
    }

    if (error) {
        console.warn(error);

        return;
    }

    const dm = Math.max(Number(decimals), 0);
    const i = Math.min(
        Math.max(Math.floor(Math.log(bytes) / Math.log(K)), 0),
        sizes.length - 1,
    );

    return `${toNumber((bytes / K ** i).toFixed(dm))}${sizes[i]}`;
};

/**
 * Возвращает `value`, если оно находится в диапазоне между `min` и `max`.
 * Вернет `min`, если `value` меньше `min`.
 * Вернет `max`, если `value` больше `max`.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns number
 */
export const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/**
 * Возвращает `value`, если его длина меньше `max`.
 * Иначе обрезает `value` до длины `max`, добавляя `ellipsis` в конец строки.
 * @param {string} value
 * @param {number} max
 * @param {string} ellipsis
 * @returns string
 */
export const textClamp = (value, max, ellipsis = '...') => (value.length > max
    ? `${value.slice(0, max - ellipsis.length)}${ellipsis}`
    : value);

/**
 * Получение типа чего угодно
 * https://tc39.es/ecma262/#sec-object.prototype.tostring
 * @param {any} value
 * @returns string
 */
export const toRawType = value => Object.prototype.toString
    .call(value)
    .slice(8, -1);

/**
 * Проверяет является ли переданное значение валидной датой
 * @param {date} value
 * @returns boolean
 */
export const isValidDate = value => value instanceof Date && !isNaN(value);

/**
 * Обрезает концевые слэши, кроме случая, когда передана строка `/`
 * @param {string} value
 * @returns string
 */
export const trimTrailingSlash = value => {
    if (toRawType(value) !== 'String' || value === '/') return value;

    // Записываем индекс последнего символа
    let i = value.length - 1;

    // Уменьшаем индекс на 1 пока текущий символ по индексу равен `/`
    while (value[i] === '/') i--;

    // Возвращаем строку, обрезанную до получившегося индекса
    return value.slice(0, i + 1);
};

/**
 * Проверяет была ли зажата специальная клавиша в момент срабатывания события
 * @param {object} event
 * @returns boolean
 */
export const checkIsMetaKeyPressed = (event = {}) => {
    const { metaKey, altKey, ctrlKey, shiftKey } = event;

    return Boolean(metaKey || altKey || ctrlKey || shiftKey);
};
